import { BaseWebComponentController, IListenerConfig, WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { XdrSearchLiveWebComponent } from '../component/XdrSearhLiveWebComponent';
import { XdrSearchLiveView } from '../view/XdrSearchLiveView';
import { templateXdrSearchLive } from '../view/template';
import {
  DEFAULT_VIEW_ID,
  LIVESEARCH_ITEM_TYPES,
  LivesearchEvents,
  LiveSearchFilters,
  LiveSearchPivotingObject,
  QueryExecutionResponse,
  SMART_VIEW_MAX_INPUT_LENGTH,
  SmartViewItem
} from '../types';
import { getRequestDefaultOptions } from '../../../utils/request.utils';
import { htmlDecode, htmlEncode } from '../../../utils/escape.utils';
import { GzToast, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { ResultView, SearchEvents } from '../../xdrSearch/types';
import { SearchQueryPanelEvents } from '../../xdrSearchQueryPanel/types';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { MaxLengthValidator } from '../../../ui/gzForm/validators/max-length.validator';
import { MinLengthValidator } from '../../../ui/gzForm/validators/min-length.validator';
import { Subscription } from 'rxjs';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { QueryExecution } from '../../../xdr/liveSearch/types';
import _ from 'lodash';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { SmartViewEvents, SmartViewItemConfiguration, SmartViewsCreateRequest, SmartViewsRecentCreateRequest, SmartViewsUpdateRequest, SmartViewType, ValidateViewNameErrorCodeResponse } from '../../../grid/genericGridSmartViews/types';
import { isQueryExecutionFinished } from '../../../xdr/liveSearch/utils';
import { FILTER_CHANGED, TARGET_FILTERS_TYPES } from '../../xdrLiveSearchQueryPanel/types/constants';
import { ResizeDirection } from '../../../types/common/types';
import { SideMenuWidthValues } from '../../../ui/gzSideMenu/types';
import { SchemaHelperWebComponent } from '../../../xdr/liveSearch/schemaHelper';
import { SearchEvents as SearchEventsGzQuery } from '../../../ui/gzQueryInput/types/index';
import { EXPORT_CSV_RESULT_METADATA_EVENT } from '../../../xdr/liveSearch/metadataGrid/types/index';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import { PRESET_QUERIES_TEMPLATES } from '../types/PresetQueries';

export class XdrSearchLiveController extends BaseWebComponentController {
  public component: XdrSearchLiveWebComponent;
  public view: XdrSearchLiveView;
  private selectedSmartViewItem: SmartViewItem;
  private refreshInterval: NodeJS.Timeout | null;
  private lastDisplayedQueryExecution: QueryExecution | null;
  private waitingForResults = false;
  private hasQueryRun = false;
  private lastDisplayedSchemaHelper: SchemaHelperWebComponent | null = null;
  private pivotingParams: LiveSearchPivotingObject | null = null;

  constructor(component: XdrSearchLiveWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: XdrSearchLiveView,
      viewTemplate: templateXdrSearchLive,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$wrapper,
        event: LivesearchEvents.SMART_VIEWS_LOADED,
        callback: this.onSmartViewsLoaded,
      },
      {
        element: this.view.dom.$searchQueryPanel,
        event: SearchEvents.RUN_QUERY,
        callback: this.onRunQuery,
      },
      {
        element: this.view.dom.$gridSmartViews,
        event: SmartViewEvents.SMART_VIEW_AFTER_DELETE,
        callback: this.onSmartViewsAfterDelete,
      },
      {
        element: this.view.dom.$gridSmartViews,
        event: SmartViewEvents.SMART_VIEW_SELECTED,
        callback: this.onClickSmartViewItem,
      },
      {
        element: this.view.dom.$gridSmartViews,
        event: SmartViewEvents.SMART_VIEW_AFTER_RECENT_CREATE,
        callback: this.onCreateRecentSmartViewItem,
      },
      {
        element: this.view.dom.$gridSmartViews,
        event: SmartViewEvents.SMART_VIEW_AFTER_CREATE,
        callback: this.onCreateSmartViewItem,
      },
      {
        element: this.view.dom.$gridSmartViews,
        event: SmartViewEvents.SMART_VIEW_DELETED,
        callback: this.onDeleteSmartViewItem,
      },
      {
        element: this.view.dom.$gridSmartViews,
        event: SmartViewEvents.SMART_VIEW_NAME_UPDATED,
        callback: this.onRenameSmartViewItem,
      },
      {
        element: this.view.dom.$gridSmartViews,
        event: SmartViewEvents.SMART_VIEW_SAVED,
        callback: this.onSaveSmartView,
      },
      {
        element: this.view.dom.$searchQueryPanel,
        event: SearchQueryPanelEvents.SAVE_AS,
        callback: this.onSaveAs,
      },
      {
        element: this.view.dom.$searchQueryPanel,
        event: SearchQueryPanelEvents.SAVE,
        callback: this.onSaveAs,
      },
      {
        element: this.view.dom.$searchQueryPanel,
        event: SearchQueryPanelEvents.DISCARD,
        callback: this.onDiscard,
      },
      {
        element: this.view.dom.$searchQueryPanel,
        event: SearchQueryPanelEvents.RESET_FILTERS,
        callback: this.onResetFilters,
      },
      {
        element: this.view.dom.$searchQueryPanel,
        event: 'change',
        callback: this.onPanelChanged,
      },
      {
        element: this.view.dom.$searchQueryPanel,
        event: FILTER_CHANGED,
        callback: this.onFilterChange,
      },
      {
        element: this.view.dom.$searchQueryPanel,
        event: SearchQueryPanelEvents.EXPORT_CSV,
        callback: this.onExportCsv,
      },
      {
        element: this.view.dom.$searchQueryPanel,
        event: EXPORT_CSV_RESULT_METADATA_EVENT,
        callback: this.onExportResultMetadataCsv,
      },
      {
        element: this.view.dom.$searchQueryPanel,
        event: SearchEventsGzQuery.HELP_FOR_QUERY_ON_CLICK,
        callback: this.onHelp,
      },
    ];
  }

  /**
   * listen to change events from panel (filters + input)
   */
  private async onPanelChanged(ev: WebComponentEvent): Promise<void> {
    if (!this.selectedSmartViewItem) {
      return;
    }
    const query = (await this.view.dom.$searchQueryPanel.getQueryValue()).raw;
    this.view.dom.$searchQueryPanel.shouldDisableRunButton(this.checkInProgressExecution() || isEmpty(query));
    this.view.dom.$searchQueryPanel.shouldDisableClearButton(isEmpty(query));
    this.view.setModifiedStateValue({ queryContent: query });

    this.view.dom.$searchQueryPanel.enableDisableButtonsForSelectedView(
      query,
      this.selectedSmartViewItem.content,
      this.view.getCurrentStateOfCurrentItem(),
      this.view.getInitialStateOfCurrentItem(),
      this.selectedSmartViewItem.hasResults,
      this.isSelectedViewReadOnly()
    );
  }

  private isSelectedViewReadOnly(): boolean {
    return this.selectedSmartViewItem.isRecent || this.selectedSmartViewItem.isReadOnly;
  }

  private onExportCsv(): void {
    const url = this.dependencies.getExportToCsvUrl({
        queryExecutionId: this.selectedSmartViewItem.lastExecution?._id,
      }),
      link = document.createElement('a');

    link.setAttribute('href', url);
    link.setAttribute('download', 'liveSearchResults.csv');
    document.body.appendChild(link);

    link.click();
    document.body.removeChild(link);
  }

  private onExportResultMetadataCsv(): void {
    const url = this.dependencies.getExportResultMetadataToCsvUrl({
        queryExecutionId: this.selectedSmartViewItem.lastExecution?._id,
      }),
      link = document.createElement('a');

    link.setAttribute('href', url);
    link.setAttribute('download', 'liveSearchMetadataResults.csv');
    document.body.appendChild(link);

    link.click();
    document.body.removeChild(link);
  }

  private async onDiscard(): Promise<void> {
    let initialContent = '';
    if (this.selectedSmartViewItem && this.selectedSmartViewItem.content) {
      initialContent = this.selectedSmartViewItem.content;
      this.view.setModifiedStateValue({ queryContent: initialContent });
    }
    this.view.dom.$searchQueryPanel.setQueryValue(initialContent);
    this.view.dom.$searchQueryPanel.removeModifyMarkerFromTitle();
    await this.onResetFilters();
  }

  private async onResetFilters(): Promise<void> {
    const filters = this.view.dom.$gridSmartViews.getActiveView().specifics.targetFilters;
    this.view.resetFiltersOfCurrentStateToInitial();
    if (_.isEqual(this.view.getCurrentStateOfCurrentItem(), this.view.getInitialStateOfCurrentItem())) {
      this.view.dom.$searchQueryPanel.removeModifyMarkerFromTitle();
    }
    this.view.dom.$searchQueryPanel.enableDisableButtonsForSelectedView(
      this.selectedSmartViewItem.content,
      this.selectedSmartViewItem.content,
      this.view.getCurrentStateOfCurrentItem(),
      this.view.getInitialStateOfCurrentItem(),
      this.selectedSmartViewItem.hasResults,
      this.isSelectedViewReadOnly()
    );
    await this.buildLiveSearchFilters(filters);
  }

  private onSaveAs(ev: any): void {
    const maxNumberOfCharacters = SMART_VIEW_MAX_INPUT_LENGTH;
    const minNumberOfCharacters = 1;
    const { windowBodyHtml, windowFooterHtml } = this.getSaveAsPopUpTemplate(ev.type);

    const windowTitle =
      ev.type === SearchQueryPanelEvents.SAVE_AS
        ? this.dependencies.translateFn('Save as new query?')
        : this.dependencies.translateFn('Save changes?');
    const saveBtn = windowFooterHtml.querySelector('#confirm') as GzButtonWebComponent;
    const cancelBtn = windowFooterHtml.querySelector('#cancel') as GzButtonWebComponent;
    const name = windowBodyHtml.querySelector('#name') as GzFormFieldWebComponent;
    let formGroup: FormGroup | null;
    if (ev.type === SearchQueryPanelEvents.SAVE && this.selectedSmartViewItem) {
      name.value = this.selectedSmartViewItem.name;
    }
    name.Validators = [
      RequiredValidator(this.dependencies.translateFn('This field is required.')),
      MaxLengthValidator(
        maxNumberOfCharacters,
        this.dependencies.translateFn('The query name must have a maximum of {nameMaxLength} characters.', {
          nameMaxLength: maxNumberOfCharacters,
        })
      ),
      MinLengthValidator(
        minNumberOfCharacters,
        this.dependencies.translateFn('The query name must have a minimum of {nameMinLength} characters.', {
          nameMinLength: minNumberOfCharacters,
        })
      ),
    ];
    formGroup = FormGroup.build({
      name,
    });

    const subscription: Subscription = formGroup.valid$.subscribe({
      next: valid => {
        saveBtn.disabled = !valid;
      },
    });

    const dialog = GzDialogWebComponent.buildDialogElement({
      headline: windowTitle,
      bodyContent: windowBodyHtml,
      footerContent: windowFooterHtml,
      isOpen: true,
    });

    saveBtn.addEventListener('click', async () => {
      await this.onConfirmSaveQuery(formGroup, subscription, dialog, ev.type);
    });

    cancelBtn.addEventListener('click', () => {
      dialog.close();
    });
  }

  private onHelp(): void {
    this.showSchemaViewerPanel();
  }

  private showSchemaViewerPanel(): void {
    const xdrSearch = document.body.querySelector('gz-xdr-search-tab-panel');
    const xdrSearchShadow = xdrSearch?.shadowRoot;
    const sideMenu: any = xdrSearchShadow?.querySelector('gz-side-menu');

    if (this.lastDisplayedSchemaHelper === null) {
      this.lastDisplayedSchemaHelper = new SchemaHelperWebComponent();
      this.lastDisplayedSchemaHelper.style.height = '100%';

      this.lastDisplayedSchemaHelper.injectDependencies(this.dependencies);
      this.lastDisplayedSchemaHelper.start().then().catch();
    }

    sideMenu.setMenuContent(this.lastDisplayedSchemaHelper);
    sideMenu.openSideMenu();

    sideMenu.closeOnClickOutside = true;
    sideMenu.resizable = ResizeDirection.LEFT;
    sideMenu.menuWidth = SideMenuWidthValues.MEDIUM;
  }

  private async onConfirmSaveQuery(formGroup, subscription, dialog, originEv): Promise<void> {
    const formValues = formGroup?.value,
      query = await this.view.dom.$searchQueryPanel.getQueryValue(),
      queryName = htmlEncode(formValues.name),
      closeModal = (): void => {
        formGroup?.unsubscribe();
        subscription?.unsubscribe();
        dialog.close();
      };

    this.view.dom.$searchQueryPanel.removeModifyMarkerFromTitle();
    this.view.dom.$searchQueryPanel.grid.unsetQueryExecution();
    this.view.dom.$searchQueryPanel.liveSearchMetadata.unsetQueryExecution();

    if (originEv === SearchQueryPanelEvents.SAVE_AS) {
      try {
        await this.createSavedViewFromContent(query.raw, queryName);
        closeModal();
      } catch (e) {
        if (Object.values(ValidateViewNameErrorCodeResponse).includes(e?.code)) {
          formGroup.children.name.markAsInvalid(this.view.getSmartViewNameValidationErrorMessage(e.code));
        } else {
          closeModal();
          this.buildToastNotification(
            this.dependencies.translateFn('Failed to create {queryName}', { queryName }),
            ToastSeverity.ERROR
          );
        }
      }
    } else {
      closeModal();
      if (
        await this.updateViewWithContent(
          this.selectedSmartViewItem.id,
          query.raw,
          queryName,
          this.selectedSmartViewItem.isRecent
        )
      ) {
        this.buildToastNotification(this.dependencies.translateFn('Query saved successfully'), ToastSeverity.SUCCESS);
        await this.displayActiveSmartViewItem();
      }
    }
  }

  private getSaveAsPopUpTemplate(eventOrigin: string): any {
    const windowBodyHtml = document.createElement('div');
    const windowFooterHtml = document.createElement('div');
    let description,
      readonlyAttribute,
      requiredAttribute,
      warning = '';
    if (eventOrigin === SearchQueryPanelEvents.SAVE_AS) {
      description = this.dependencies.translateFn('Your changes will be saved as a new query.');
      warning = '';
      requiredAttribute = 'required';
    } else {
      readonlyAttribute = 'readonly';
      description = this.dependencies.translateFn('Your changes will overwrite the existing query.');
      warning = `<gz-info-box severity="warning" >${this.dependencies.translateFn(
        'Existing results will be discarded from selected query.'
      )}</gz-info-box>`;
    }

    windowBodyHtml.innerHTML = `<p>
        ${description}
      </p>
     <gz-form-field label="${this.dependencies.translateFn(
       'Name'
     )}" id="name" class='pt-5' ${requiredAttribute} ${readonlyAttribute} style="--form-field-label-width: 100px;">
         <gz-input gz-form-input type="text" inputWidth="full" value="" ${readonlyAttribute}></gz-input>
         <gz-input-error></gz-input-error>
    </gz-form-field>
    <div class="warning" class='pt-3'>
        ${warning}
    </div>`;

    windowFooterHtml.innerHTML = `<gz-button id='confirm' type="full" color="primary" class='mr-2'>
            ${this.dependencies.translateFn('Save#preserve state')}
        </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('Cancel')}
      </gz-button>`;

    return {
      windowBodyHtml,
      windowFooterHtml,
    };
  }

  private async displayActiveSmartViewItem(): Promise<void> {
    const activeView = this.view.dom.$gridSmartViews.getActiveView();
    await this.displaySmartViewConfiguration(activeView);

    const filters = activeView.specifics.targetFilters;
    await this.buildLiveSearchFilters(filters);
  }

  private async onSmartViewsLoaded(event: WebComponentEvent): Promise<void> {
    if (this.pivotingParams) {
      await this.displayPivotingSmartViewItem();
      this.pivotingParams = null;
      return;
    }

    await this.displayActiveSmartViewItem();
  }

  private async displayPivotingSmartViewItem(): Promise<void> {
    let queryContent: string | null = null;
    const smartViewConfiguration = await this.getSmartViewConfiguration();

    await this.displaySmartViewConfiguration(smartViewConfiguration);
    this.view.dom.$gridSmartViews.controller?.view.dom.$smartViewsLit.updateActiveItem(smartViewConfiguration);

    if (smartViewConfiguration && this.pivotingParams?.presetQuery) {
      const presetQueryTemplate = PRESET_QUERIES_TEMPLATES[this.pivotingParams.presetQuery];

      if (!presetQueryTemplate) {
        this.buildToastNotification(this.dependencies.translateFn('The preset query does not exist.'), ToastSeverity.ERROR);
      }

      queryContent = this.replaceTemplateParameters(
        presetQueryTemplate ?? '',
        this.pivotingParams.presetQueryParams
      );
      this.view.dom.$searchQueryPanel.setQueryValue(queryContent);
    }

    const targetFilters: LiveSearchFilters = {
      companyId: this.pivotingParams?.companyId as string,
      companyName: this.pivotingParams?.companyName as string,
      operatingSystems: this.pivotingParams?.operatingSystems as number[],
      endpoints: this.pivotingParams?.endpointIds as string[],
      tags: []
    };

    await this.buildLiveSearchFilters(targetFilters);
    this.setCurrentStateAndRenderActions(queryContent, targetFilters);
    this.view.dom.$gridSmartViews.controller?.view.dom.$smartViewsLit.focusOnActiveItem();
  }

  private async getSmartViewConfiguration(): Promise<SmartViewItemConfiguration | null | undefined> {
    if (this.pivotingParams?.predefinedQuery) {
      const smartView = this.view.dom.$gridSmartViews.controller?.getViewByLabelOrId(this.pivotingParams.predefinedQuery);
      if (smartView) {
        return smartView;
      }

      // if the predefined query does not exist, we display an error toast and return the default view
      this.buildToastNotification(this.dependencies.translateFn('The predefined query does not exist.'), ToastSeverity.ERROR);
    }

    return this.view.dom.$gridSmartViews.controller?.getViewByLabelOrId(DEFAULT_VIEW_ID, true) as SmartViewItemConfiguration;
  }

  private replaceTemplateParameters(template: string, params: Object | null): string {
    if (!params) {
      return template;
    }

    Object.keys(params).forEach(key => {
      const regexp = new RegExp(`{${key}}`, 'g');
      template = template.replace(regexp, params[key]);
    });

    return template;
  }

  private setCurrentStateAndRenderActions(queryContent: string | null, targetFilters: LiveSearchFilters): void {
    if (queryContent) {
      this.view.currentStateOfCurrentItem.queryContent = queryContent;
    }

    this.view.currentStateOfCurrentItem.targetFilters = targetFilters;
    this.view.renderModifiedState();
    this.view.dom.$searchQueryPanel.enableDisableButtonsForSelectedView(
      this.view.getCurrentStateOfCurrentItem().queryContent,
      this.view.getInitialStateOfCurrentItem().queryContent,
      this.view.getCurrentStateOfCurrentItem(),
      this.view.getInitialStateOfCurrentItem(),
      this.selectedSmartViewItem.hasResults,
      this.isSelectedViewReadOnly()
    );
  }

  private async onDeleteSmartViewItem(event: WebComponentEvent): Promise<void> {
    this.buildToastNotification(this.dependencies.translateFn('Query deleted successfully'), ToastSeverity.SUCCESS);
  }

  private buildToastNotification(message: string, severity: ToastSeverity): void {
    const toastNotificationConfigs: GzToast = {
      message,
      hasAction: false,
      severity: severity,
      keepOpen: false,
      autoCloseDelayMs: 10000,
    };

    GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
  }

  private async onSaveSmartView(event: WebComponentEvent): Promise<void> {}

  private async onRenameSmartViewItem(event: WebComponentEvent): Promise<void> {
    if (this.selectedSmartViewItem && event.detail.smartViewItemConfiguration._id === this.selectedSmartViewItem.id) {
      this.view.dom.$searchQueryPanel.pageTitle = htmlDecode(event.detail.updatedViewName);
      this.selectedSmartViewItem.name = htmlDecode(event.detail.updatedViewName);
    }
  }

  private async createRecentViewFromContent(
    newContent: string,
    oldContent: string,
    label: string | null = null,
    shouldSelect: boolean = false,
    isParentEdited: boolean | null = true
  ): Promise<any> {
    const createView: SmartViewsRecentCreateRequest = {
      category: LIVESEARCH_ITEM_TYPES.RECENT,
      type: SmartViewType.CUSTOM,
      specifics: {
        queryContent: newContent,
        isQueryEdited: newContent !== oldContent,
        targetFilters: this.getTargetFilters(),
      },
    };

    if (isParentEdited !== null) {
      createView.specifics.isQueryEdited = isParentEdited || createView.specifics.isQueryEdited;
    }

    if (label) {
      createView.label = label;
    }

    return this.view.dom.$gridSmartViews.addRecentView(createView, shouldSelect);
  }

  private async createSavedViewFromContent(content: string, label: string): Promise<SmartViewItemConfiguration | null> {
    const createView: SmartViewsCreateRequest = {
      category: LIVESEARCH_ITEM_TYPES.SAVED,
      type: SmartViewType.CUSTOM,
      label: label,
      specifics: {
        queryContent: content,
        targetFilters: this.getTargetFilters(),
      },
    };

    return this.view.dom.$gridSmartViews.saveSmartViewDirectly(createView);
  }

  private async updateViewWithContent(
    id: string,
    content: string,
    label: string | null = null,
    isRecent: boolean = false
  ): Promise<SmartViewItemConfiguration | null> {
    const updateView: SmartViewsUpdateRequest = {
      id: id,
      details: {
        specifics: {
          queryContent: content,
          targetFilters: this.getTargetFilters(),
        },
      },
    };
    if (label) {
      updateView.details.label = label;
    }
    let modifiedView;
    if (isRecent) {
      modifiedView = await this.view.dom.$gridSmartViews.updateRecentView(updateView);
    } else {
      modifiedView = await this.view.dom.$gridSmartViews.updateSmartView(updateView);
    }

    await this.view.dom.$gridSmartViews.reloadCategories();
    await this.switchSmartViewItem(modifiedView);

    return modifiedView;
  }

  private isActiveViewNewQuery(): boolean {
    return this.selectedSmartViewItem.id === DEFAULT_VIEW_ID;
  }

  private shouldSwitchItemOnRun(): boolean {
    return this.selectedSmartViewItem.isRecent;
  }

  private async onRunQuery(): Promise<void> {
    const content = await this.view.dom.$searchQueryPanel.getQueryValue();

    if (isEmpty(content.raw)) {
      return;
    }

    this.hasQueryRun = true;

    const hasParent = !this.isActiveViewNewQuery(),
      strContent = content.raw;
    let item;

    try {
      this.view.dom.$searchQueryPanel.shouldDisableRunButton(true);
      await this.displayBasedOnQueryExecution(ResultView.IN_PROGRESS);
      item = await this.createRecentViewFromContent(
        strContent,
        this.selectedSmartViewItem.content,
        hasParent
          ? this.selectedSmartViewItem.isRecent
            ? this.selectedSmartViewItem.originalName
            : this.selectedSmartViewItem.name
          : null,
        this.shouldSwitchItemOnRun(),
        hasParent && this.selectedSmartViewItem.isEdited !== undefined ? this.selectedSmartViewItem.isEdited : null
      );
    } catch (error: any) {
      const errorMessage =
        error.exceptionType && error.exceptionType.startsWith('Exceptions\\LiveSearch\\')
          ? error.message
          : this.dependencies.translateFn('Error');

      this.buildToastNotification(errorMessage, ToastSeverity.ERROR);
      await this.displayBasedOnQueryExecution(ResultView.NO_RESULTS);
      this.view.dom.$searchQueryPanel.shouldDisableRunButton(false);
      return;
    }

    await this.periodicWaitForResults(item._id);
    this.view.dom.$searchQueryPanel.resetManualResize();
  }

  private async periodicWaitForResults(smartViewId: string, initialCheck: boolean = true): Promise<void> {
    if (this.waitingForResults) {
      return;
    }
    this.waitingForResults = true;
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (initialCheck) {
      await this.checkForResults(smartViewId);
    }
    this.refreshInterval = setInterval(async () => {
      await this.checkForResults(smartViewId);
    }, this.dependencies.getLiveSearchCheckForResultsIntervalInSec() * 1000);
  }

  private async checkForResults(smartViewId: string): Promise<void> {
    const queryExecution = await this.displayBasedOnQueryExecution(ResultView.IN_PROGRESS, smartViewId);
    const hasResults = this.hasResultsFromExecution(queryExecution);

    this.selectedSmartViewItem.hasResults = hasResults;
    this.view.dom.$searchQueryPanel.shouldDisableExportButton(!hasResults);
    if (this.checkStopWaitingForResults(queryExecution)) {
      this.stopWaitingForResults();
    }
  }

  private checkStopWaitingForResults(queryExecution: QueryExecution | null): boolean {
    if (!queryExecution) {
      return true;
    }
    return isQueryExecutionFinished(queryExecution);
  }

  private stopWaitingForResults(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.view.dom.$searchQueryPanel.shouldDisableRunButton(false);
    }
    this.waitingForResults = false;
  }

  private hasResultsFromExecution(queryExecution: QueryExecution | null): boolean {
    return queryExecution && !queryExecution.hasExpiredResults && queryExecution.resultsFields;
  }

  async getQueryExecutionBasedOnResultView(
    resultView: ResultView,
    smartViewId: string = ''): Promise<QueryExecutionResponse | null> {
    if (smartViewId !== '' && resultView === ResultView.IN_PROGRESS) {
      return await this.getLastQueryExecution(smartViewId);
    }
    if (resultView === ResultView.INIT) {
      return null;
    }
    const baseQueryExecution: QueryExecutionResponse = {
      success: true,
      data: {
        _id: '',
        smartViewId: '',
        companyId: '',
        userId: '',
        content: '',
        doNotStoreResultsAfter: '',
        deleteResultsAt: '',
        hasExpiredResults: false,
        hasExpiredResultsMetadata: false,
        deleteResultsMetadataAt: '',
        hasFinishedStoringResults: false,
      },
    };
    if (resultView === ResultView.NO_RESULTS) {
      return baseQueryExecution;
    }
    baseQueryExecution.data.doNotStoreResultsAfterTimestamp = Date.now() / 1000 + 3600;

    return baseQueryExecution;
  }

  private async displayBasedOnQueryExecution(
    resultView: ResultView,
    smartViewId: string = '',
  ): Promise<QueryExecution | null> {
    const queryExecutionResponse = await this.getQueryExecutionBasedOnResultView(resultView, smartViewId);
    let queryExecution = null as QueryExecution | null,
      hasResultsFields = false;

    if (queryExecutionResponse && queryExecutionResponse.success && queryExecutionResponse.data) {
      this.view.dom.$searchQueryPanel.liveSearchMetadata.setQueryExecution(queryExecutionResponse.data);

      if (_.isEqual(queryExecutionResponse.data, this.lastDisplayedQueryExecution)) {
        //nothing has changed
        return queryExecutionResponse.data;
      } else {
        if (queryExecutionResponse.data._id !== this.lastDisplayedQueryExecution?._id) {
          this.view.dom.$searchQueryPanel.hideCountdown();
        }
      }
      this.lastDisplayedQueryExecution = queryExecutionResponse.data;
      if (this.selectedSmartViewItem) {
        this.selectedSmartViewItem.lastExecution = queryExecutionResponse.data;
      }

      queryExecution = queryExecutionResponse.data;
      hasResultsFields = queryExecution.resultsFields !== undefined;
      this.view.dom.$searchQueryPanel.grid.setQueryExecution(queryExecution);
      if (this.hasQueryRun) {
        this.view.dom.$searchQueryPanel.setTextboxCompact();
      }
      if (
        queryExecution.deleteResultsAtTimestamp &&
        queryExecution.deleteResultsAtTimestamp > Date.now() / 1000 &&
        !isQueryExecutionFinished(queryExecution)
      ) {
        this.view.dom.$searchQueryPanel.createInProgressCountdownFromExecution(
          queryExecution.doNotStoreResultsAfterTimestamp
        );
      }
      if (hasResultsFields) {
        if (queryExecution.deleteResultsAtTimestamp && isQueryExecutionFinished(queryExecution)) {
          this.view.dom.$searchQueryPanel.createResultCountdownFromExecution(queryExecution.deleteResultsAtTimestamp);
        } else if (queryExecution.hasExpiredResults) {
          //expired results
          this.view.dom.$searchQueryPanel.showCountdownText();
          this.view.dom.$searchQueryPanel.liveSearchMetadata.unsetQueryExecution();
        }
      } else {
        if (isQueryExecutionFinished(queryExecution)) {
          // query execution is finished
          this.view.dom.$searchQueryPanel.hideCountdown();
        }
      }
    } else {
      //failed or no query execution
      this.view.dom.$searchQueryPanel.setTextboxExpanded();
      this.lastDisplayedQueryExecution = null;
      this.view.dom.$searchQueryPanel.unsetQueryExecution();
    }

    return queryExecution;
  }

  private async setLastSelectedSmartViewItem(smItemConfiguration: any): Promise<void> {
    const content = smItemConfiguration.specifics?.queryContent ? smItemConfiguration.specifics.queryContent : '',
      queryExecution = await this.displayBasedOnQueryExecution(smItemConfiguration.isRecent ? ResultView.IN_PROGRESS : ResultView.INIT, smItemConfiguration._id);
    this.selectedSmartViewItem = {
      id: smItemConfiguration._id,
      content: content,
      name: smItemConfiguration.label,
      originalName: smItemConfiguration.specifics.originalLabel ?? null,
      hasResults: this.hasResultsFromExecution(queryExecution),
      lastExecution: queryExecution,
      companyId: smItemConfiguration.companyId,
      isRecent: smItemConfiguration.isRecent,
      isReadOnly: smItemConfiguration.isReadOnly,
      isEdited: smItemConfiguration.specifics.isQueryEdited,
      targetFilters: smItemConfiguration.specifics?.targetFilters,
    };
    this.view.setActiveViewItem(smItemConfiguration);
  }

  private async displaySmartViewConfiguration(smItemConfiguration: any): Promise<void> {
    if (!smItemConfiguration) {
      return;
    }
    await this.setLastSelectedSmartViewItem(smItemConfiguration);
    this.view.dom.$searchQueryPanel.setQueryValue(htmlDecode(this.selectedSmartViewItem.content));
    this.view.dom.$searchQueryPanel.pageTitle = htmlDecode(this.selectedSmartViewItem.name);
    this.view.dom.$searchQueryPanel.enableDisableButtonsForSelectedView(
      this.selectedSmartViewItem.content,
      this.selectedSmartViewItem.content,
      this.view.getCurrentStateOfCurrentItem(),
      this.view.getInitialStateOfCurrentItem(),
      this.selectedSmartViewItem.hasResults,
      this.isSelectedViewReadOnly()
    );
  }

  private async onCreateRecentSmartViewItem(event: WebComponentEvent): Promise<void> {
    //only switch to new item if the previous item was recent
    if (this.shouldSwitchItemOnRun()) {
      await this.switchSmartViewItem(event.detail.createdView);
    }
  }

  private async onCreateSmartViewItem(event: WebComponentEvent): Promise<void> {
    await this.switchSmartViewItem(event.detail.createdView);
  }

  private async onSmartViewsAfterDelete(event: WebComponentEvent): Promise<void> {
    const deletedSmartViewConfiguration = event.detail.deletedView;
    const activeView = this.view.dom.$gridSmartViews.getActiveView();
    if (this.selectedSmartViewItem.id === deletedSmartViewConfiguration._id) {
      if (activeView) {
        await this.switchSmartViewItem(activeView);
      }
    }
  }

  private async onClickSmartViewItem(event: WebComponentEvent): Promise<void> {
    const selectedSmartViewConfiguration = event.detail.smartViewConfiguration;
    if (this.view.dom.$gridSmartViews.getActiveView() === selectedSmartViewConfiguration._id) {
      return;
    }
    await this.switchSmartViewItem(selectedSmartViewConfiguration);
    this.hasQueryRun = false;
    this.view.dom.$searchQueryPanel.setTextboxExpanded();
  }

  private async switchSmartViewItem(selectedSmartViewConfiguration: any): Promise<void> {
    this.view.dom.$gridSmartViews.setActiveViewIdInState(selectedSmartViewConfiguration._id);
    this.stopWaitingForResults();

    let filters = selectedSmartViewConfiguration.specifics.targetFilters;
    filters = this.overwriteCompanyFilter(selectedSmartViewConfiguration, filters);

    await this.displaySmartViewConfiguration(selectedSmartViewConfiguration);
    this.view.dom.$searchQueryPanel.resetManualResize();
    await this.buildLiveSearchFilters(filters);

    if (this.lastDisplayedQueryExecution && !isQueryExecutionFinished(this.lastDisplayedQueryExecution)) {
      await this.periodicWaitForResults(selectedSmartViewConfiguration._id, false);
    }
    this.view.dom.$searchQueryPanel.liveSearchMetadata.controller.setPageTitle(
      this.view.dom.$searchQueryPanel.pageTitle
    );
  }

  async getLastQueryExecution(smartViewId: string): Promise<QueryExecutionResponse> {
    return this.dependencies.api.clients.LiveSearchQueryExecutions.getLastQueryExecution(
      {
        smartViewId: smartViewId,
      },
      getRequestDefaultOptions()
    );
  }

  private checkInProgressExecution(): boolean {
    return this.selectedSmartViewItem && this.selectedSmartViewItem.lastExecution
      ? !isQueryExecutionFinished(this.selectedSmartViewItem.lastExecution)
      : false;
  }

  private getTargetFilters(): object {
    const filters: any = {
      companyId: this.dependencies.isPartnerCompany()
        ? this.view.dom.$searchQueryPanel.companySelector.selectedTarget.companyId
        : this.dependencies.getUserCompany().companyId,
      companyName: this.dependencies.isPartnerCompany()
        ? this.view.dom.$searchQueryPanel.companySelector.selectedTarget.companyName
        : this.dependencies.getUserCompany().companyName,
      endpoints: this.view.dom.$searchQueryPanel.getSelectedEndpoints(),
      tags: this.view.dom.$searchQueryPanel.getSelectedTags(),
      operatingSystems: this.view.dom.$searchQueryPanel.operatingSystemFilter.selectedFilterOptions,
    };

    return filters;
  }

  private onFilterChange(ev: WebComponentEvent): void {
    switch (ev.detail.target) {
      case TARGET_FILTERS_TYPES.ENDPOINT:
        this.view.setModifiedStateValue({
          targetFilters: {
            endpoints: ev.detail.endpoints
              .map(endpoint => {
                return endpoint.value;
              })
              .sort(),
          },
        });
        break;
      case TARGET_FILTERS_TYPES.COMPANY:
        this.view.setModifiedStateValue({
          targetFilters: {
            companyId: ev.detail.companyId,
            companyName: ev.detail.companyName,
          },
        });
        break;
      case TARGET_FILTERS_TYPES.OPERATING_SYSTEM:
        this.view.setModifiedStateValue({
          targetFilters: {
            operatingSystems: ev.detail.operatingSystems,
          },
        });
        break;
      case TARGET_FILTERS_TYPES.TAG:
        this.view.setModifiedStateValue({
          targetFilters: {
            tags: ev.detail.tags,
          },
        });
        break;
      default:
        break;
    }
    this.view.dom.$searchQueryPanel.enableDisableButtonsForSelectedView(
      this.selectedSmartViewItem.content,
      this.selectedSmartViewItem.content,
      this.view.getCurrentStateOfCurrentItem(),
      this.view.getInitialStateOfCurrentItem(),
      this.selectedSmartViewItem.hasResults,
      this.isSelectedViewReadOnly()
    );
  }

  /**
   * called externally to set the company id
   */
  public async setSelectedFilterBasedOnChangedCompany(company: Target) {
    if (!this.shouldOverwriteCompanyFilters(this.selectedSmartViewItem)) {
      return;
    }
    this.view.initialStateOfCurrentItem['targetFilters']['companyId'] = company.companyId;
    this.view.initialStateOfCurrentItem['targetFilters']['companyName'] = company.companyName;
    await this.view.dom.$searchQueryPanel.setSelectedCompany(company);
  }

  public setPivotingParams(pivotingParams: LiveSearchPivotingObject, pivotingToLoadedComponent: boolean | null): void {
    this.pivotingParams = pivotingParams;

    if (pivotingToLoadedComponent) {
      this.render().then();
    }
  }

  private async buildLiveSearchFilters(filters: LiveSearchFilters): Promise<any> {
    filters = this.overwriteCompanyFilter(this.selectedSmartViewItem, filters);
    const startPromises: Promise<void>[] = [];
    this.view.dom.$searchQueryPanel.setOptionsForOperatingSystemsFilter(filters.operatingSystems);
    if (this.dependencies.isPartnerCompany()) {
      startPromises.push(
        this.view.dom.$searchQueryPanel.buildCompanySelectorDropdown(filters.companyId, filters.companyName)
      );
    }
    startPromises.push(
      this.view.dom.$searchQueryPanel.setSelectedTags(filters.companyId, filters.tags ? filters.tags : [])
    );
    startPromises.push(
      this.view.dom.$searchQueryPanel.updateEndpointFilterProps(filters.companyId, filters.endpoints as any)
    );
    await Promise.all(startPromises);
  }

  /**
   * overwrites the values for the company filter in the filters array based on smartview and current user
   */
  private overwriteCompanyFilter(smartView: any, filters: LiveSearchFilters): LiveSearchFilters {
    if (this.shouldOverwriteCompanyFilters(smartView)) {
      const activeCompany = this.dependencies.getActiveCompany();
      filters.companyId = activeCompany.companyId;
      filters.companyName = activeCompany.companyName;
    }
    return filters;
  }

  private shouldOverwriteCompanyFilters(smartView: any): boolean {
    return smartView.isReadOnly && this.dependencies.getUser().mdrSuperAnalyst;
  }
}
